// ── Dynamic Certificate Form Fields Normalizer ──

/**
 * Normalizes custom_form_fields from JSON, String, Sectioned Array, or Flat Array
 * into a standardized Sectioned Array structure: [{ section_id, section_title, section_icon, fields: [...] }]
 * Returns [] if no fields have been configured by admin.
 */
export function normalizeCustomFormFields(raw) {
  let parsed = raw;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
  }

  if (!parsed || (Array.isArray(parsed) && parsed.length === 0)) {
    return [];
  }

  if (Array.isArray(parsed)) {
    // Check if it's already a Sectioned Array (element has 'fields' array)
    const isSectioned = parsed.some(item => item && Array.isArray(item.fields));
    if (isSectioned) {
      return parsed.map((sec, idx) => ({
        section_id: sec.section_id || `sec_${idx + 1}`,
        section_title: sec.section_title || sec.title || `विभाग ${idx + 1}`,
        section_icon: sec.section_icon || sec.icon || '📌',
        fields: Array.isArray(sec.fields) ? sec.fields.map(normalizeFieldObject) : [],
      }));
    }

    // Flat array of fields -> wrap into a single section
    return [
      {
        section_id: 'default_section',
        section_title: '📌 अर्जाची माहिती (Application Details)',
        section_icon: '📌',
        fields: parsed.map(normalizeFieldObject),
      },
    ];
  }

  return [];
}

export const certificateInputType = type => {
  const normalized = String(type || 'text').toLowerCase();
  return normalized === 'calculated' ? 'text' : normalized;
};

function normalizeFieldObject(f) {
  return {
    key: String(f.key || f.name || `field_${Math.random().toString(36).substr(2, 5)}`).trim(),
    label: String(f.label || f.name || f.key || 'नवीन इनपुट').trim(),
    type: String(f.type || 'text').toLowerCase(),
    required: (f.required ?? f.is_required ?? false) !== false,
    is_bilingual: Boolean(f.is_bilingual || f.bilingual),
    options: Array.isArray(f.options) ? f.options : (typeof f.options === 'string' ? f.options.split(',').map(s => s.trim()).filter(Boolean) : []),
    placeholder: f.placeholder ? String(f.placeholder) : '',
    inputMode: f.inputMode || (f.type === 'number' ? 'decimal' : undefined),
    minLength: f.minLength != null && !isNaN(f.minLength) ? Number(f.minLength) : undefined,
    maxLength: f.maxLength != null && !isNaN(f.maxLength) ? Number(f.maxLength) : undefined,
    min: f.min != null && !isNaN(f.min) ? Number(f.min) : undefined,
    max: f.max != null && !isNaN(f.max) ? Number(f.max) : undefined,
    pattern: f.pattern ? String(f.pattern) : undefined,
    errorMessage: f.errorMessage || f.error_message || undefined,
    readonly: Boolean(f.readonly || f.type === 'calculated'),
    calculation: normalizeCalculation(f.calculation),
    visible_when: normalizeCondition(f.visible_when),
    required_when: normalizeCondition(f.required_when),
  };
}

function normalizeCalculation(value) {
  if (!value || typeof value !== 'object' || value.function !== 'age_on_date') return undefined;
  const dateField = String(value.date_field || '').trim();
  const asOfField = String(value.as_of_field || '').trim();
  return dateField ? { function: 'age_on_date', date_field: dateField, as_of_field: asOfField || undefined } : undefined;
}

function normalizeCondition(value) {
  if (!value || typeof value !== 'object') return undefined;
  if (Array.isArray(value.all)) return { all: value.all.map(normalizeCondition).filter(Boolean) };
  if (Array.isArray(value.any)) return { any: value.any.map(normalizeCondition).filter(Boolean) };
  const field = String(value.field || '').trim();
  const operator = String(value.operator || 'equals').trim();
  return field ? { field, operator, value: value.value } : undefined;
}

export function calculateAgeOnDate(dateOfBirth, asOfDate) {
  if (!dateOfBirth || !asOfDate) return '';
  const birth = new Date(`${dateOfBirth}T00:00:00Z`);
  const target = new Date(`${asOfDate}T00:00:00Z`);
  if (Number.isNaN(birth.getTime()) || Number.isNaN(target.getTime()) || birth > target) return '';
  let age = target.getUTCFullYear() - birth.getUTCFullYear();
  if (target.getUTCMonth() < birth.getUTCMonth()
    || (target.getUTCMonth() === birth.getUTCMonth() && target.getUTCDate() < birth.getUTCDate())) age -= 1;
  return age;
}

export function evaluateCertificateCondition(condition, values) {
  if (!condition) return true;
  if (Array.isArray(condition.all)) return condition.all.every(item => evaluateCertificateCondition(item, values));
  if (Array.isArray(condition.any)) return condition.any.some(item => evaluateCertificateCondition(item, values));
  const actual = values?.[condition.field];
  const expected = condition.value;
  switch (condition.operator) {
    case 'not_equals': return String(actual ?? '') !== String(expected ?? '');
    case 'in': return (Array.isArray(expected) ? expected : [expected]).map(String).includes(String(actual ?? ''));
    case 'not_in': return !(Array.isArray(expected) ? expected : [expected]).map(String).includes(String(actual ?? ''));
    case 'greater_than': return Number(actual) > Number(expected);
    case 'less_than': return Number(actual) < Number(expected);
    case 'is_empty': return actual === undefined || actual === null || actual === '';
    case 'is_not_empty': return actual !== undefined && actual !== null && actual !== '';
    default: return String(actual ?? '') === String(expected ?? '');
  }
}

export function resolveCertificateValues(sections, values) {
  const resolved = { ...(values || {}) };
  sections.flatMap(section => section.fields || []).forEach(field => {
    if (field.type === 'calculated' && field.calculation?.function === 'age_on_date') {
      resolved[field.key] = calculateAgeOnDate(
        resolved[field.calculation.date_field],
        resolved[field.calculation.as_of_field],
      );
    }
  });
  return resolved;
}

export function isCertificateFieldVisible(field, values) {
  return evaluateCertificateCondition(field.visible_when, values);
}

export function isCertificateFieldRequired(field, values) {
  return Boolean(field.required || (field.required_when && evaluateCertificateCondition(field.required_when, values)));
}
